import React from 'react';
import { FaArrowUp, FaArrowDown, FaLock, FaBolt, FaShieldAlt } from 'react-icons/fa';
import ConfidenceMeter from './ConfidenceMeter';

export default function SignalCard({ signal, onExecute, mode }) {
  const isBullish = signal.direction === 'BULLISH';
  const isBlocked = signal.blocked;

  const dirColor = isBullish ? 'emerald' : 'red';
  const dirIcon = isBullish ? <FaArrowUp /> : <FaArrowDown />;

  return (
    <div className={`bg-white rounded-xl border p-4 ${isBlocked ? 'border-red-200 opacity-60' : 'border-gray-200'}`}>
      {/* Header */}
      <div className="flex items-center justify-between mb-3">
        <div className="flex items-center gap-2">
          <span className={`text-${dirColor}-600 text-lg`}>{dirIcon}</span>
          <div>
            <span className="text-gray-900 font-semibold text-sm">
              {signal.strike} {signal.option_type}
            </span>
            <span className={`ml-2 text-xs text-${dirColor}-600 font-medium`}>
              {signal.direction}
            </span>
          </div>
        </div>
        <div className="flex items-center gap-2">
          <ConfidenceMeter value={signal.confidence} size="sm" />
          {isBlocked && (
            <span className="px-2 py-0.5 rounded-full text-xs font-medium bg-red-100 text-red-600 flex items-center gap-1">
              <FaLock className="text-[10px]" /> Blocked
            </span>
          )}
        </div>
      </div>

      {/* Levels */}
      <div className="grid grid-cols-3 gap-2 mb-3">
        <div className="bg-gray-50 rounded-lg p-2 text-center">
          <div className="text-[10px] text-gray-400 uppercase">Entry</div>
          <div className="text-sm font-mono text-gray-900">{signal.entry?.toFixed(1)}</div>
        </div>
        <div className="bg-red-50 rounded-lg p-2 text-center">
          <div className="text-[10px] text-red-500 uppercase">Stop Loss</div>
          <div className="text-sm font-mono text-red-600">{signal.stop_loss?.toFixed(1)}</div>
        </div>
        <div className="bg-emerald-50 rounded-lg p-2 text-center">
          <div className="text-[10px] text-emerald-500 uppercase">Target</div>
          <div className="text-sm font-mono text-emerald-600">{signal.target?.toFixed(1)}</div>
        </div>
      </div>

      {/* Stats row */}
      <div className="flex items-center gap-3 mb-3 text-xs text-gray-500">
        <span>RR: <span className="text-gray-900 font-medium">{signal.rr_ratio?.toFixed(1)}</span></span>
        <span>Qty: <span className="text-gray-900 font-medium">{signal.quantity}</span></span>
        <span>Risk: <span className="text-amber-600 font-medium">{signal.risk_amount?.toFixed(0)}</span></span>
        <span>EV: <span className={`font-medium ${signal.expected_value > 0 ? 'text-emerald-600' : 'text-red-600'}`}>
          {signal.expected_value?.toFixed(0)}
        </span></span>
      </div>

      {/* Strategy & Regime */}
      <div className="flex items-center gap-2 mb-3 text-xs">
        <span className="px-2 py-0.5 rounded bg-blue-100 text-blue-700">
          {signal.strategy?.replace(/_/g, ' ')}
        </span>
        <span className="px-2 py-0.5 rounded bg-gray-100 text-gray-600">
          {signal.regime}
        </span>
        <span className="text-gray-400">
          P: {(signal.probability * 100).toFixed(0)}%
        </span>
      </div>

      {/* Reasoning */}
      <div className="mb-3">
        <div className="text-[10px] text-gray-400 uppercase mb-1">Reasoning</div>
        <div className="space-y-0.5">
          {(signal.reasoning || []).slice(0, 4).map((r, i) => (
            <div key={i} className="text-xs text-gray-600 flex items-start gap-1">
              <span className="text-blue-500 mt-0.5">•</span> {r}
            </div>
          ))}
          {(signal.reasoning || []).length > 4 && (
            <div className="text-xs text-gray-400">+{signal.reasoning.length - 4} more</div>
          )}
        </div>
      </div>

      {/* Block reasons */}
      {isBlocked && signal.block_reasons?.length > 0 && (
        <div className="mb-3 p-2 rounded bg-red-50 border border-red-200">
          <div className="text-[10px] text-red-600 uppercase mb-1 flex items-center gap-1">
            <FaShieldAlt /> Risk Firewall
          </div>
          {signal.block_reasons.map((r, i) => (
            <div key={i} className="text-xs text-red-500">{r}</div>
          ))}
        </div>
      )}

      {/* Greeks */}
      <div className="flex items-center gap-3 mb-3 text-[10px] text-gray-400">
        <span>Δ {signal.delta?.toFixed(3)}</span>
        <span>Γ {signal.gamma?.toFixed(5)}</span>
        <span>Θ {signal.theta?.toFixed(2)}</span>
        <span>IV {signal.iv?.toFixed(1)}%</span>
      </div>

      {/* Execute button */}
      {!isBlocked && mode !== 'MANUAL' && (
        <button
          onClick={() => onExecute?.(signal)}
          className="w-full bg-blue-600 text-white py-2 rounded-lg text-sm font-medium flex items-center justify-center gap-2 hover:bg-blue-700 transition-colors"
        >
          <FaBolt className="text-xs" />
          {mode === 'SEMI_AUTO' ? 'Confirm & Execute' : 'Auto-Executing...'}
        </button>
      )}
      {!isBlocked && mode === 'MANUAL' && onExecute && (
        <button
          onClick={() => onExecute?.(signal)}
          className="w-full py-2 rounded-lg text-sm font-medium bg-gray-100 hover:bg-gray-200 text-gray-700 transition-colors"
        >
          Execute Manually
        </button>
      )}
    </div>
  );
}
